'use client';

import { c as _c } from "react/compiler-runtime";
import { type OptionTabGroup, TabGroup } from '@morpho-org/ui-v6/client';
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';
import { ScrollSection } from '@/components/common/ScrollSection';
import SubTabsStickyBackground from '@/components/common/SubTabsStickyBackground';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useSectionScrollSpy } from '@/hooks/useSectionScrollSpy';
const NAVBAR_OFFSET = 64;
const TAB_BAR_HEIGHT = 64;
export interface ScrollSectionTabsSection<TTab extends string> {
  id: TTab;
  label: string;
  slot: ReactNode;
  /** Hide this section when the wallet is not connected. */
  requiresConnectedWallet?: boolean;
}
export interface ScrollSectionTabsProps<TTab extends string> {
  segmentId: string;
  dataTestId: string;
  sections: ScrollSectionTabsSection<TTab>[];
  options: OptionTabGroup<TTab>[];
  /** Section that `#yourPosition` deep-link + disconnect-reset logic target. */
  yourPositionSectionId: TTab;
  fallbackSectionId: TTab;
  defaultSectionId: TTab;
  /**
   * Called only on a genuine tab click. Scroll-spy and deep-link changes move
   * the active tab without going through here, so this stays a click signal.
   */
  onTabChange?: (tab: TTab) => void;
}

/**
 * Shared tab header + ScrollSection wrapper used by MarketTabs. Handles sticky
 * offset, wallet-hydration deep-links, and
 * auto-scrolls away from the connected-only section on disconnect.
 */
export default function ScrollSectionTabs(t0) {
  const $ = _c(27);
  const {
    segmentId,
    dataTestId,
    sections,
    options,
    yourPositionSectionId,
    fallbackSectionId,
    defaultSectionId,
    onTabChange
  } = t0;
  const {
    account: t1
  } = useWeb3Context();
  const {
    isConnected
  } = t1;
  const previousIsConnected = useRef(isConnected);
  const stickyOffset = NAVBAR_OFFSET + TAB_BAR_HEIGHT;
  let t2;
  if ($[0] !== isConnected) {
    t2 = t3 => {
      const {
        requiresConnectedWallet
      } = t3;
      return !requiresConnectedWallet || isConnected;
    };
    $[0] = isConnected;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const visibleSections = sections.filter(t2);
  const scrollSpySections = visibleSections.map(_temp);
  const {
    activeSection,
    scrollToSection,
    registerSection
  } = useSectionScrollSpy({
    sections: scrollSpySections,
    defaultSection: defaultSectionId,
    stickyOffset
  });
  let t3;
  let t4;
  if ($[2] !== activeSection || $[3] !== fallbackSectionId || $[4] !== isConnected || $[5] !== scrollToSection || $[6] !== yourPositionSectionId) {
    t3 = () => {
      const wasConnected = previousIsConnected.current;
      previousIsConnected.current = isConnected;
      if (!isConnected && wasConnected && activeSection === yourPositionSectionId) {
        scrollToSection(fallbackSectionId);
      }
      if (isConnected && !wasConnected) {
        const hash = window.location.hash.slice(1);
        if (hash === yourPositionSectionId) {
          requestAnimationFrame(() => scrollToSection(yourPositionSectionId));
        }
      }
    };
    t4 = [isConnected, activeSection, scrollToSection, yourPositionSectionId, fallbackSectionId];
    $[2] = activeSection;
    $[3] = fallbackSectionId;
    $[4] = isConnected;
    $[5] = scrollToSection;
    $[6] = yourPositionSectionId;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== onTabChange || $[10] !== scrollToSection) {
    t5 = sectionId => {
      onTabChange?.(sectionId);
      scrollToSection(sectionId);
    };
    $[9] = onTabChange;
    $[10] = scrollToSection;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleTabClick = t5;
  const t6 = "gap-xl flex w-full flex-col";
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      "--sticky-offset": `${stickyOffset}px`
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = t7 as CSSProperties;
  let t9;
  if ($[13] !== activeSection || $[14] !== handleTabClick || $[15] !== options || $[16] !== segmentId) {
    t9 = <TabGroup id={segmentId} options={options} value={activeSection} onChange={handleTabClick} hasActiveBorder={true} className="w-full" />;
    $[13] = activeSection;
    $[14] = handleTabClick;
    $[15] = options;
    $[16] = segmentId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== dataTestId || $[19] !== t9) {
    t10 = <SubTabsStickyBackground data-testid={dataTestId} topPosition={0}>{t9}</SubTabsStickyBackground>;
    $[18] = dataTestId;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== registerSection) {
    t11 = t12 => {
      const {
        id: id_0,
        label: label_0,
        slot
      } = t12;
      return <ScrollSection key={id_0} id={id_0} label={label_0} stickyOffset={stickyOffset} onRegister={registerSection}>{slot}</ScrollSection>;
    };
    $[21] = registerSection;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = visibleSections.map(t11);
  let t13;
  if ($[23] !== t10 || $[24] !== t12 || $[25] !== t8) {
    t13 = <div className={t6} style={t8}>{t10}{t12}</div>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
function _temp(t0) {
  const {
    id,
    label
  } = t0;
  return {
    id,
    label
  };
}